import { FieldProps } from "formik";
import { Autocomplete } from "formik-mui";
import {
  AutocompleteRenderInputParams,
  debounce,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { ApiError, EntsorgerInfo, EntsorgerListe } from "../../api";
import { useGetEntsorger, useSucheEntsorger } from "../../client/hooks";

interface EntsorgerSelectProps extends FieldProps {
  label: string;
  required?: boolean;
}

function EntsorgerSelect({
  label,
  required,
  field,
  form,
  meta,
}: EntsorgerSelectProps) {
  const searchEntsorger = useSucheEntsorger();
  const ladeEntsorger = useGetEntsorger();
  const [optionen, setOptionen] = useState<ReadonlyArray<EntsorgerInfo>>([]);
  const [inputValue, setInputValue] = useState(field.value);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    form.setFieldValue(field.name, inputValue);
  }, [inputValue]);

  useEffect(() => {
    if (field.value && typeof field.value === "string") {
      ladeEntsorger(field.value)
        .then((entsorger) => {
          form.setFieldValue(field.name, entsorger);
          setInputValue(entsorger);
        })
        .catch((error) => {
          console.error("Error loading entsorger:", error);
        });
    }
  }, [field.value]);

  const ladeOptionen = useMemo(
    () =>
      debounce(
        async (
          request: { input: string },
          callback: (error?: ApiError, results?: EntsorgerListe) => void
        ) =>
          searchEntsorger({
            search: request.input,
            page: 0,
            limit: 20,
            sort: "nummer",
            isArchiviert: false,
          })
            .then((results) => {
              callback(undefined, results);
            })
            .catch((error) => callback(error)),
        400
      ),
    [searchEntsorger]
  );

  useEffect(() => {
    ladeOptionen({ input: searchValue }, (error, results) => {
      if (error) console.error("Error:", error);
      setOptionen([...(results ? results.items : [])]);
    });
  }, [searchValue, ladeOptionen]);

  return (
    <Autocomplete
      field={field}
      form={form}
      meta={meta}
      filterSelectedOptions={true}
      filterOptions={(x) => x} // Deaktiviert die Filterlogik, damit komplett nur in dem Backend gesucht werden kann.
      options={optionen}
      getOptionLabel={(option: string | EntsorgerInfo) => {
        if (typeof option === "string") {
          return "";
        } else {
          return option.nummer;
        }
      }}
      onChange={(event, value) => {
        setInputValue(value);
      }}
      onInputChange={(event, value) => {
        setSearchValue(value);
      }}
      renderOption={(props, option: EntsorgerInfo) => {
        return (
          <ListItem {...props} key={option.id}>
            <ListItemText
              primary={
                <Stack>
                  <Typography>{option.nummer}</Typography>
                  <Typography>{option.materialienName}</Typography>
                </Stack>
              }
              secondary={option.abfallartKey}
            />
          </ListItem>
        );
      }}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <TextField {...params} label={label + (required ? " *" : "")} />
      )}
    />
  );
}

export default EntsorgerSelect;
